import type { ToggleSwitchControllerPolicy } from "../../state/toggle-switch-controller.context";

const POLICIES: readonly ToggleSwitchControllerPolicy[] = ["exactlyOne", "multi", "zeroOrOne"];

function isValidPolicyToken(token: string): token is ToggleSwitchControllerPolicy {
    return (POLICIES as readonly string[]).includes(token);
}

function logPolicyError(message: string): void {
    console.error(`[ToggleSwitchController policy] ${message}`);
}

export type ParsedToggleSwitchControllerPolicy = {
    /**
     * @param innerWidth viewport width from `window.innerWidth`, or `undefined` during SSR /
     *   before the client snapshot is available — treated like “below the smallest breakpoint”
     *   for tier selection (leading/base tier only).
     */
    resolve: (innerWidth: number | undefined) => ToggleSwitchControllerPolicy;
};

/**
 * Parses `ToggleSwitchController` `policy` strings: `{policy}[,{width}:{policy}]*`
 * (trimmed; insignificant whitespace; up to two breakpoint pairs; invalid segments dropped with `console.error`).
 */
export function parseToggleSwitchControllerPolicy(raw: string): ParsedToggleSwitchControllerPolicy {
    const trimmedAll = raw.trim();
    if (trimmedAll === "") {
        logPolicyError("Policy string is empty; using fallback policy \"multi\".");
        return { resolve: () => "multi" };
    }

    const parts = trimmedAll.split(",");
    const leadingRaw = parts[0].trim();

    let validBase: ToggleSwitchControllerPolicy | null = null;
    if (leadingRaw !== "") {
        if (isValidPolicyToken(leadingRaw)) {
            validBase = leadingRaw;
        } else {
            logPolicyError(`Ignoring invalid leading policy token "${leadingRaw}".`);
        }
    } else {
        logPolicyError("Ignoring empty leading policy token.");
    }

    const breakpointByWidth = new Map<number, ToggleSwitchControllerPolicy>();
    let acceptedValidPairSegmentCount = 0;
    const badWidthGoodPolicyInOrder: ToggleSwitchControllerPolicy[] = [];

    for (let i = 1; i < parts.length; i++) {
        const segment = parts[i].trim();
        if (segment === "") {
            logPolicyError(`Ignoring empty policy segment at comma-separated index ${i}.`);
            continue;
        }

        const colonMatch = /^(\S+)\s*:\s*(.+)$/.exec(segment);
        if (!colonMatch) {
            logPolicyError(`Ignoring malformed breakpoint segment "${segment}" (expected "{integer}:{policy}").`);
            continue;
        }

        const widthToken = colonMatch[1].trim();
        const policyToken = colonMatch[2].trim();

        if (!/^\d+$/.test(widthToken)) {
            if (isValidPolicyToken(policyToken)) {
                badWidthGoodPolicyInOrder.push(policyToken);
            }
            logPolicyError(`Ignoring breakpoint segment "${segment}" (width "${widthToken}" is not a valid integer).`);
            continue;
        }

        if (!isValidPolicyToken(policyToken)) {
            logPolicyError(`Ignoring breakpoint segment "${segment}" (unknown policy "${policyToken}").`);
            continue;
        }

        const width = Number(widthToken);
        if (acceptedValidPairSegmentCount >= 2) {
            logPolicyError(
                `Ignoring extra breakpoint pair "${segment}" (at most two comma-separated width tiers are allowed after the leading policy).`,
            );
            continue;
        }
        acceptedValidPairSegmentCount += 1;
        breakpointByWidth.set(width, policyToken);
    }

    const salvageFlat: ToggleSwitchControllerPolicy | null =
        validBase === null && breakpointByWidth.size === 0 && badWidthGoodPolicyInOrder.length > 0
            ? badWidthGoodPolicyInOrder[badWidthGoodPolicyInOrder.length - 1]!
            : null;

    const resolve = (innerWidth: number | undefined): ToggleSwitchControllerPolicy => {
        let fromBreakpoint: ToggleSwitchControllerPolicy | null = null;
        let bestW = -Infinity;

        if (innerWidth !== undefined) {
            for (const [w, pol] of breakpointByWidth) {
                if (w <= innerWidth && w >= bestW) {
                    bestW = w;
                    fromBreakpoint = pol;
                }
            }
        }

        if (fromBreakpoint !== null) {
            return fromBreakpoint;
        }
        if (validBase !== null) {
            return validBase;
        }
        if (salvageFlat !== null) {
            return salvageFlat;
        }
        return "multi";
    };

    return { resolve };
}
